import React, { useState, useCallback, useMemo, useRef } from "react";
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  Card,
  Typography,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  message,
  Badge,
  Spin,
  DatePicker,
  Statistic,
  Tooltip,
  Divider,
  InputNumber,
  AutoComplete,
  Tabs,
  Dropdown,
  FloatButton,
  Flex,
  Avatar,
  Empty,
  Alert,
} from "antd";
import {
  PlusOutlined,
  EyeOutlined,
  ReloadOutlined,
  FileTextOutlined,
  DollarOutlined,
  CloseCircleOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  MinusCircleOutlined,
  MoreOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  MoneyCollectOutlined,
  BarChartOutlined,
  WalletOutlined,
  CreditCardOutlined,
  BankOutlined,
  QrcodeOutlined,
  PrinterOutlined,
  SearchOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { receiptService } from "../services/receiptService";
import type { Receipt } from "../types";
import moment from "moment";
import { customerService } from "../services/customerService";
import { courseService } from "../services/courseService";
import Bill from "../components/receipt/Bill";
import ReactToPrint from "react-to-print";
import { exportReciptToExcel } from "../utils/excelExport";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Search } = Input;

interface GetReceiptsParams {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  customer?: string;
  paymentStatus?: string;
  search?: string;
}

interface ReceiptFormData {
  customer: any;
  items: any[];
  currency: string;
  paymentMethod: string;
  discount: number;
  notes: string;
}

const ReceiptsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [exportLoading, setExportLoading] = useState(false);

  // const printRef = useRef<HTMLDivElement>(null);

  const printComponentRef = useRef<HTMLDivElement>(null);
  const reactToPrintContent = useRef<any>(null);

  // States
  const [searchParams, setSearchParams] = useState<GetReceiptsParams>({
    page: 1,
    limit: 10,
  });
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(
    moment().format("YYYY-MM-DD")
  );
  const [receiptFormData, setReceiptFormData] = useState<ReceiptFormData>({
    customer: null,
    items: [],
    currency: "LAK",
    paymentMethod: "cash",
    discount: 0,
    notes: "",
  });

  const [dataPrint, setDataPrint] = useState<any | null>(null);

  console.log("dataPrint--->", dataPrint);

  const [searchText, setSearchText] = useState<string>("");
  const [customerSearchText, setCustomerSearchText] = useState<string>("");
  const [searchCourse, setSearchCourse] = useState<string>("");
  // Forms
  const [paymentForm] = Form.useForm();
  const [voidForm] = Form.useForm();
  const [createForm] = Form.useForm();

  // Queries
  const {
    data: receiptsResponse,
    isLoading,
    refetch,
  } = useQuery(
    ["receipts", searchParams],
    () => receiptService.getReceipts(searchParams),
    {
      keepPreviousData: true,
      onError: (_: any) => {
        message.error("❌ ບໍ່ສາມາດໂຫລດຂໍ້ມູນໄດ້");
      },
    }
  );

  const { data: dailyReportData, isLoading: isDailyReportLoading } = useQuery(
    ["dailyReceipts", selectedDate],
    () => receiptService.getDailyReceipts(selectedDate),
    {
      enabled: activeModal === "daily-report" && !!selectedDate,
      onError: (_: any) => {
        message.error("❌ ບໍ່ສາມາດໂຫລດລາຍງານປະຈຳວັນໄດ້");
      },
    }
  );

  const { data: customersData } = useQuery(["customers"], () =>
    customerService.getCustomers({ limit: 1000 })
  );

  const { data: coursesData } = useQuery(["courses", searchCourse], () =>
    courseService.getCourses({ limit: 1000, search: searchCourse })
  );

  // Mutations
  const updatePaymentMutation = useMutation(
    ({ id, data }: { id: string; data: any }) =>
      receiptService.updateReceipt(id, data),
    {
      onSuccess: () => {
        message.success("✅ ອັບເດດການຊຳລະເງິນສຳເລັດ");
        queryClient.invalidateQueries(["receipts"]);
        closeModal();
      },
      onError: (error: any) => {
        message.error(
          `❌ ${error?.response?.data?.message || "ບໍ່ສາມາດອັບເດດການຊຳລະໄດ້"}`
        );
      },
    }
  );

  const voidReceiptMutation = useMutation(
    ({ id, reason }: { id: string; reason: string }) =>
      receiptService.voidReceipt(id, reason),
    {
      onSuccess: () => {
        message.success("✅ ຍົກເລິກບິນເກັບເງິນສຳເລັດ");
        queryClient.invalidateQueries(["receipts"]);
        closeModal();
      },
      onError: (error: any) => {
        message.error(
          `❌ ${error?.response?.data?.message || "ບໍ່ສາມາດຍົກເລິກໄດ້"}`
        );
      },
    }
  );

  const createReceiptMutation = useMutation(
    (data: any) => receiptService.createReceipt(data),
    {
      onSuccess: () => {
        message.success("✅ ສ້າງບິນເກັບເງິນສຳເລັດ");
        queryClient.invalidateQueries(["receipts"]);
        closeModal();
        resetReceiptForm();
      },
      onError: (error: any) => {
        message.error(
          `❌ ${error?.response?.data?.message || "ບໍ່ສາມາດສ້າງບິນເກັບເງິນໄດ້"}`
        );
      },
    }
  );

  // Helper functions
  const openModal = useCallback(
    (modalType: string, receipt?: Receipt) => {
      setActiveModal(modalType);
      if (receipt) {
        setSelectedReceipt(receipt);
        if (modalType === "payment") {
          paymentForm.setFieldsValue({
            amountPaid: 0,
            paymentMethod: receipt.paymentMethod,
            notes: receipt.notes,
          });
        }
      }
    },
    [paymentForm]
  );

  const handleSearchCourse = (value: string) => {
    setSearchCourse(value);
  };

  const closeModal = useCallback(() => {
    setActiveModal(null);
    setSelectedReceipt(null);
    paymentForm.resetFields();
    voidForm.resetFields();
    createForm.resetFields();
  }, [paymentForm, voidForm, createForm]);

  const resetReceiptForm = useCallback(() => {
    setReceiptFormData({
      customer: null,
      items: [],
      currency: "LAK",
      paymentMethod: "cash",
      discount: 0,
      notes: "",
    });
    setCustomerSearchText("");
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
    setSearchParams((prev) => ({ ...prev, search: value, page: 1 }));
  }, []);

  const handleDateRangeChange = useCallback((dates: any) => {
    setSearchParams((prev) => ({
      ...prev,
      startDate: dates?.[0]?.format("YYYY-MM-DD"),
      endDate: dates?.[1]?.format("YYYY-MM-DD"),
      page: 1,
    }));
  }, []);

  const handleStatusFilter = useCallback((status?: string) => {
    setSearchParams((prev) => ({ ...prev, paymentStatus: status, page: 1 }));
  }, []);

  const handleTableChange = useCallback((pagination: any) => {
    setSearchParams((prev) => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
    }));
  }, []);

  // Receipt form handlers
  const addItemToReceipt = useCallback(
    (course: any, packageOption: any, quantity: number = 1) => {
      setReceiptFormData((prev) => {
        const existingIndex = prev.items.findIndex(
          (item) =>
            item.course._id === course._id &&
            item.packageOption.sessions === packageOption.sessions
        );

        if (existingIndex >= 0) {
          const newItems = [...prev.items];
          newItems[existingIndex].quantity += quantity;
          return { ...prev, items: newItems };
        } else {
          return {
            ...prev,
            items: [...prev.items, { course, packageOption, quantity }],
          };
        }
      });
    },
    []
  );

  const removeItemFromReceipt = useCallback((index: number) => {
    setReceiptFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  }, []);

  const updateItemQuantity = useCallback((index: number, quantity: number) => {
    setReceiptFormData((prev) => {
      const newItems = [...prev.items];
      newItems[index].quantity = Math.max(1, quantity);
      return { ...prev, items: newItems };
    });
  }, []);

  // Calculations
  const calculateSubtotal = useCallback(
    (currency: string) => {
      return receiptFormData.items.reduce((total, item) => {
        return total + item.packageOption.price[currency] * item.quantity;
      }, 0);
    },
    [receiptFormData.items]
  );

  // const calculateTotal = useCallback(
  //   (currency: string, discount: number = 0) => {
  //     const subtotal = calculateSubtotal(currency);
  //     const discountAmount = (subtotal * discount) / 100;
  //     return subtotal - discountAmount;
  //   },
  //   [calculateSubtotal]
  // );

  // Utility functions
  const getFilteredCustomers = useMemo(() => {
    return customersData?.data?.customers?.filter(
      (customer: any) =>
        customer.name
          .toLowerCase()
          .includes(customerSearchText.toLowerCase()) ||
        customer.customerId
          .toLowerCase()
          .includes(customerSearchText.toLowerCase()) ||
        customer.phone.includes(customerSearchText)
    );
  }, [customersData, customerSearchText]);

  const getPaymentStatusConfig = (status: string) => {
    const configs = {
      pending: {
        color: "warning",
        text: "⏳ ລໍຖ້າຊຳລະ",
        bgColor: "#fff7e6",
        borderColor: "#ffa940",
      },
      partial: {
        color: "processing",
        text: "💰 ຊຳລະບາງສ່ວນ",
        bgColor: "#e6f7ff",
        borderColor: "#40a9ff",
      },
      paid: {
        color: "success",
        text: "✅ ຊຳລະແລ້ວ",
        bgColor: "#f6ffed",
        borderColor: "#73d13d",
      },
      refunded: {
        color: "error",
        text: "🔄 ຄືນເງິນ",
        bgColor: "#fff2f0",
        borderColor: "#ff7875",
      },
    };
    return configs[status as keyof typeof configs] || configs.pending;
  };

  const getPaymentMethodConfig = (method: string) => {
    const configs = {
      cash: {
        text: "💵 ເງິນສົດ",
        icon: <MoneyCollectOutlined />,
        color: "#52c41a",
      },
      transfer: {
        text: "🏦 ໂອນເງິນ",
        icon: <BankOutlined />,
        color: "#1890ff",
      },
      qr: { text: "📱 QR Code", icon: <QrcodeOutlined />, color: "#722ed1" },
      card: {
        text: "💳 ບັດສະມາຊິກ",
        icon: <CreditCardOutlined />,
        color: "#fa8c16",
      },
    };
    return configs[method as keyof typeof configs] || configs.cash;
  };

  const getCurrencySymbol = (currency: string) => {
    const symbols = {
      LAK: "₭",
      THB: "฿",
      USD: "$",
    };
    return symbols[currency as keyof typeof symbols] || currency;
  };

  const formatCurrency = (amount: number, currency: string) => {
    return `${new Intl.NumberFormat("lo-LA").format(
      amount
    )} ${getCurrencySymbol(currency)}`;
  };

  // Form handlers
  const handlePaymentSubmit = useCallback(
    (values: any) => {
      if (!selectedReceipt) return;
      updatePaymentMutation.mutate({
        id: selectedReceipt._id,
        data: values,
      });
    },
    [selectedReceipt, updatePaymentMutation]
  );

  const handleVoidSubmit = useCallback(
    (values: any) => {
      if (!selectedReceipt) return;
      voidReceiptMutation.mutate({
        id: selectedReceipt._id,
        reason: values.voidReason,
      });
    },
    [selectedReceipt, voidReceiptMutation]
  );

  const handleCreateReceiptSubmit = useCallback(
    (values: any) => {
      if (!receiptFormData.customer) {
        message.error("ກະລຸນາເລືອກລູກຄ້າ");
        return;
      }

      if (receiptFormData.items.length === 0) {
        message.error("ກະລຸນາເລືອກບໍລິການຢ່າງນ້ອຍ 1 ລາຍການ");
        return;
      }

      const receiptData = {
        customer: receiptFormData.customer._id,
        currency: values.currency,
        paymentMethod: values.paymentMethod,
        items: receiptFormData.items.map((item) => ({
          course: item.course._id,
          packageOption: {
            sessions: item.packageOption.sessions,
            price: item.packageOption.price,
          },
          quantity: item.quantity,
        })),
        discount: values.discount || 0,
        notes: values.notes,
      };

      createReceiptMutation.mutate(receiptData);
    },
    [receiptFormData, createReceiptMutation]
  );

  const handlePrint = async (record: Receipt) => {
    await setDataPrint(record);
    reactToPrintContent.current?.handlePrint();
  };

  // Action menu
  const getActionMenu = (record: Receipt) => ({
    items: [
      {
        key: "view",
        label: "👁️ ເບິ່ງລາຍລະອຽດ",
        icon: <EyeOutlined />,
        onClick: () => openModal("detail", record),
      },
      {
        key: "print",
        label: "🖨️ ພິມບິນ",
        icon: <PrinterOutlined />,
        onClick: () => {
          handlePrint(record);
          // Handle print
        },
      },
      ...(!record.voidedAt && record.paymentStatus !== "paid"
        ? [
            {
              type: "divider" as const,
            },
            {
              key: "payment",
              label: "💰 ອັບເດດການຊຳລະ",
              icon: <DollarOutlined />,
              onClick: () => openModal("payment", record),
            },
          ]
        : []),
      ...(!record.voidedAt
        ? [
            {
              type: "divider" as const,
            },
            {
              key: "void",
              label: "❌ ຍົກເລິກ",
              icon: <CloseCircleOutlined />,
              danger: true,
              onClick: () => openModal("void", record),
            },
          ]
        : []),
    ],
  });

  // Table columns
  const columns = [
    {
      title: "📋 ເລກທີໃບບິນ",
      dataIndex: "receiptNumber",
      key: "receiptNumber",
      width: 200,
      render: (receiptNumber: string) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Avatar
            size={24}
            icon={<FileTextOutlined />}
            style={{ backgroundColor: "#1890ff", marginRight: 8 }}
          />
          <Text code strong>
            {receiptNumber}
          </Text>
        </div>
      ),
    },
    {
      title: "👤 ລູກຄ້າ",
      dataIndex: "customer",
      key: "customer",
      render: (customer: any) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Avatar
            size={32}
            icon={<UserOutlined />}
            style={{ backgroundColor: "#87d068", marginRight: 12 }}
          />
          <div>
            <div style={{ fontWeight: 500, color: "#1890ff" }}>
              {customer?.name || "N/A"}
            </div>
            <div style={{ fontSize: "12px", color: "#666" }}>
              ID: {customer?.customerId || "N/A"}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "💰 ຍອດລວມ",
      dataIndex: "total",
      key: "total",
      width: 140,
      render: (total: number, record: Receipt) => (
        <div style={{ textAlign: "right" }}>
          <div style={{ fontWeight: 600, color: "#f56a00", fontSize: "16px" }}>
            {formatCurrency(total, record.currency)}
          </div>
          {record.voidedAt && (
            <Tag color="red" style={{ marginTop: 4 }}>
              ❌ ຍົກເລິກ
            </Tag>
          )}
        </div>
      ),
      sorter: true,
    },
    {
      title: "📊 ສະຖານະຊຳລະ",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      width: 160,
      render: (status: string, record: Receipt) => {
        const config = getPaymentStatusConfig(status);
        return (
          <div>
            <Tag
              style={{
                backgroundColor: config.bgColor,
                borderColor: config.borderColor,
                color: "#000",
                fontWeight: 500,
                padding: "4px 8px",
                borderRadius: 6,
              }}
            >
              {config.text}
            </Tag>
            {record.paymentStatus === "partial" && (
              <div style={{ fontSize: "11px", color: "#666", marginTop: 4 }}>
                💸 ຄົງເຫຼືອ: {formatCurrency(record.amountDue, record.currency)}
              </div>
            )}
          </div>
        );
      },
      filters: [
        { text: "⏳ ຖ້າຊຳລະ", value: "pending" },
        { text: "💰 ຊຳລະບາງສ່ວນ", value: "partial" },
        { text: "✅ ຊຳລະແລ້ວ", value: "paid" },
        { text: "🔄 ຄືນເງິນ", value: "refunded" },
      ],
    },
    {
      title: "💳 ວິທີຊຳລະ",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      width: 120,
      render: (method: string) => {
        const config = getPaymentMethodConfig(method);
        return (
          <Tag
            color={config.color}
            style={{
              fontWeight: 500,
              padding: "4px 8px",
              borderRadius: 6,
            }}
          >
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: "📅 ວັນທີອອກ",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date: string) => (
        <div>
          <div style={{ fontWeight: 500, color: "#1890ff" }}>
            {moment(date).format("DD/MM/YYYY")}
          </div>
          <div style={{ fontSize: "11px", color: "#666" }}>
            🕐 {moment(date).format("HH:mm")}
          </div>
        </div>
      ),
      sorter: true,
    },
    {
      title: "⚙️ ຈັດການ",
      key: "actions",
      width: 150,
      render: (_: any, record: Receipt) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Tooltip title="ເບິ່ງລາຍລະອຽດ">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openModal("detail", record)}
              style={{ borderRadius: 6 }}
            />
          </Tooltip>
          <Dropdown menu={getActionMenu(record)} trigger={["click"]}>
            <Button
              size="small"
              icon={<MoreOutlined />}
              style={{ borderRadius: 6 }}
            />
          </Dropdown>
        </div>
      ),
    },
  ];

  // Data
  const receipts = receiptsResponse?.data?.receipts || [];
  const pagination = receiptsResponse?.data?.pagination;

  // Calculate statistics
  const totalReceipts = pagination?.total || 0;
  const pendingReceipts = receipts.filter(
    (r: Receipt) => r.paymentStatus === "pending"
  ).length;
  const paidReceipts = receipts.filter(
    (r: Receipt) => r.paymentStatus === "paid"
  ).length;
  const totalRevenue = receipts.reduce(
    (sum: number, r: Receipt) =>
      sum + (r.paymentStatus === "paid" ? r.total : 0),
    0
  );

  const handleExportToExcel = async () => {
    setExportLoading(true);
    try {
      // ✅ แก้ไขตรงนี้
      if (receipts) {
        exportReciptToExcel(receipts);
        message.success("ສົ່ງອອກລາຍງານຄອສສຳເລັດ");
      } else {
        message.error("ບໍ່ມີຂໍ້ມູນສຳລັບສົ່ງອອກ");
      }
    } catch (error) {
      console.error("Export error:", error);
      message.error("ເກີດຂໍ້ຜິດພາດໃນການສົ່ງອອກ");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh" }}>
      {/* Header Section */}
      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <div>
            <Title level={2} style={{ margin: 0, color: "#1890ff" }}>
              🧾 ຈັດການໃບເກັບເງິນ
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              ລະບົບຈັດການໃບເກັບເງິນ ແລະ ການຊຳລະເງິນ
            </Text>
          </div>
          <Space>
            <Button
              icon={<BarChartOutlined />}
              onClick={() => openModal("daily-report")}
              style={{ borderRadius: 8 }}
            >
              📊 ລາຍງານປະຈຳວັນ
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isLoading}
              style={{ borderRadius: 8 }}
            />
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => openModal("create")}
              style={{
                borderRadius: 8,
                background: "linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)",
                border: "none",
                height: 48,
                paddingLeft: 24,
                paddingRight: 24,
                fontWeight: 600,
              }}
            >
              ສ້າງໃບບິນໃໝ່
            </Button>
          </Space>
        </div>

        {/* Statistics Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={6}>
            <Card
              style={{
                borderRadius: 12,
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Statistic
                title="📋 ໃບເກັບເງິນທັງໝົດ"
                value={totalReceipts}
                valueStyle={{ color: "#1890ff", fontSize: 24, fontWeight: 600 }}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card
              style={{
                borderRadius: 12,
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Statistic
                title="⏳ ຖ້າຊຳລະ"
                value={pendingReceipts}
                valueStyle={{ color: "#fa8c16", fontSize: 24, fontWeight: 600 }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card
              style={{
                borderRadius: 12,
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Statistic
                title="✅ ຊຳລະແລ້ວ"
                value={paidReceipts}
                valueStyle={{ color: "#52c41a", fontSize: 24, fontWeight: 600 }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card
              style={{
                borderRadius: 12,
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Statistic
                title="💰 ລາຍຮັບ"
                value={totalRevenue}
                valueStyle={{ color: "#722ed1", fontSize: 24, fontWeight: 600 }}
                prefix={<WalletOutlined />}
                formatter={(value) =>
                  `${new Intl.NumberFormat("lo-LA").format(Number(value))} ₭`
                }
              />
            </Card>
          </Col>
        </Row>
      </div>

      {/* Main Content Card */}
      <Card
        style={{
          borderRadius: 16,
          border: "none",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        {/* Controls Section */}
        <div style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} lg={6}>
              <Search
                placeholder="🔍 ຄົ້ນຫາໃບເກັບເງິນ, ລູກຄ້າ..."
                allowClear
                value={searchText ?? ""}
                onSearch={handleSearch}
                style={{ borderRadius: 8 }}
                size="middle"
              />
            </Col>
            <Col xs={24} lg={6}>
              <RangePicker
                style={{ width: "100%", borderRadius: 8 }}
                onChange={handleDateRangeChange}
                placeholder={["📅 ວັນທີເລີ່ມຕົ້ນ", "📅 ວັນທີສິ້ນສຸດ"]}
                size="middle"
              />
            </Col>
            <Col xs={24} lg={6}>
              <Select
                placeholder="📊 ເບິ່ງຕາມສະຖານະ"
                allowClear
                style={{ width: "100%", borderRadius: 8 }}
                onChange={handleStatusFilter}
                size="middle"
              >
                <Option value="pending">⏳ ຖ້າຊຳລະ</Option>
                <Option value="partial">💰 ຊຳລະບາງສ່ວນ</Option>
                <Option value="paid">✅ ຊຳລະແລ້ວ</Option>
                <Option value="refunded">🔄 ຄືນເງິນ</Option>
              </Select>
            </Col>
            <Col xs={24} lg={6}>
              <Flex gap={8}>
                <Tooltip title="ສົ່ງອອກຂໍ້ມູນ">
                  <Button
                    type="primary"
                    icon={<FileExcelOutlined />}
                    onClick={handleExportToExcel}
                    loading={exportLoading}
                    style={{
                      background:
                        "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
                      border: "none",
                    }}
                  >
                    ສົ່ງອອກ Excel
                  </Button>
                </Tooltip>
              </Flex>
            </Col>
          </Row>
        </div>

        <Divider style={{ margin: "24px 0" }} />

        {/* Table */}
        <Table
          columns={columns}
          dataSource={receipts}
          rowKey="_id"
          loading={isLoading}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          style={{ borderRadius: 12, overflow: "hidden" }}
          rowClassName={(record: Receipt) => {
            if (record.paymentStatus === "pending") return "row-pending";
            if (record.paymentStatus === "paid") return "row-paid";
            return "";
          }}
          pagination={{
            current: pagination?.current || 1,
            pageSize: searchParams.limit || 10,
            total: pagination?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `🧾 ສະແດງ ${range[0]}-${range[1]} ຈາກ ${total} ລາຍການ`,
            style: { marginTop: 24 },
          }}
        />
      </Card>

      {/* Floating Action Button */}
      <FloatButton
        icon={<PlusOutlined />}
        type="primary"
        style={{
          right: 24,
          bottom: 24,
          width: 56,
          height: 56,
        }}
        onClick={() => openModal("create")}
        tooltip="ສ້າງບິນເກັບເງິນໃໝ່"
      />

      <div style={{ position: "absolute", left: "-9999px", top: "0%" }}>
        <ReactToPrint
          trigger={() => <></>}
          content={() => printComponentRef.current}
          ref={reactToPrintContent}
        />
        <div ref={printComponentRef}>
          <Bill data={dataPrint} />
        </div>
      </div>

      {/* Receipt Detail Modal */}
      <Modal
        title={
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <Title level={3} style={{ margin: 0, color: "#1890ff" }}>
              🧾 ລາຍລະອຽດບິນເກັບເງິນ
            </Title>
            <Text type="secondary">{selectedReceipt?.receiptNumber}</Text>
          </div>
        }
        open={activeModal === "detail"}
        onCancel={closeModal}
        footer={[
          <Button
            key="print"
            icon={<PrinterOutlined />}
            onClick={() =>
              selectedReceipt ? handlePrint(selectedReceipt) : ""
            }
          >
            ພິມບິນ
          </Button>,
          <Button key="close" type="primary" onClick={closeModal}>
            ປິດ
          </Button>,
        ]}
        width={900}
        style={{ top: 20 }}
        styles={{ content: { borderRadius: 16 } }}
      >
        {selectedReceipt && (
          <div style={{ padding: "16px 0" }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card
                  size="small"
                  style={{
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <Statistic
                    title="👤 ລູກຄ້າ"
                    value={selectedReceipt.customer?.name || "N/A"}
                    valueStyle={{ fontSize: 16, fontWeight: 500 }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    ID: {selectedReceipt.customer?.customerId || "N/A"}
                  </Text>
                </Card>
              </Col>
              <Col span={12}>
                <Card
                  size="small"
                  style={{
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <Statistic
                    title="💰 ຍອດລວມ"
                    value={formatCurrency(
                      selectedReceipt.total,
                      selectedReceipt.currency
                    )}
                    valueStyle={{
                      fontSize: 16,
                      fontWeight: 500,
                      color: "#f56a00",
                    }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {getPaymentStatusConfig(selectedReceipt.paymentStatus).text}
                  </Text>
                </Card>
              </Col>
            </Row>

            <Divider orientation="left" style={{ margin: "24px 0 16px" }}>
              <Text strong>📋 ລາຍການສິນຄ້າ/ບໍລິການ</Text>
            </Divider>

            <Table
              dataSource={selectedReceipt.items || []}
              pagination={false}
              size="small"
              style={{ marginBottom: 16 }}
              columns={[
                {
                  title: "ຄອສ",
                  dataIndex: "course",
                  render: (course: any) => (
                    <div>
                      <Text strong>{course?.name || "N/A"}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {course?.category?.name || ""}
                      </Text>
                    </div>
                  ),
                },
                {
                  title: "ແພັກເກັດ",
                  dataIndex: "packageOption",
                  render: (pkg: any) => (
                    <Tag color="blue">{pkg?.sessions || 0} ຄັ້ງ</Tag>
                  ),
                },
                {
                  title: "ຈຳນວນ",
                  dataIndex: "quantity",
                  width: 80,
                  align: "center" as const,
                  render: (quantity: number) => (
                    <Badge
                      count={quantity}
                      style={{ backgroundColor: "#52c41a" }}
                    />
                  ),
                },
                {
                  title: "ລາຄາລວມ",
                  dataIndex: "subtotal",
                  width: 140,
                  align: "right" as const,
                  render: (subtotal: any) => (
                    <Text strong style={{ color: "#f56a00" }}>
                      {formatCurrency(
                        subtotal?.[selectedReceipt.currency] || 0,
                        selectedReceipt.currency
                      )}
                    </Text>
                  ),
                },
              ]}
            />

            <Card
              size="small"
              style={{
                backgroundColor: "#f6ffed",
                border: "1px solid #b7eb8f",
              }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>💳 ວິທິຊຳລະ:</Text>
                  <br />
                  <Tag color="green" style={{ marginTop: 4 }}>
                    {getPaymentMethodConfig(selectedReceipt.paymentMethod).text}
                  </Tag>
                </Col>
                <Col span={12}>
                  <Text strong>📅 ວັນທີອອກ:</Text>
                  <br />
                  <Text>
                    {moment(selectedReceipt.createdAt).format(
                      "DD/MM/YYYY HH:mm"
                    )}
                  </Text>
                </Col>
              </Row>

              {selectedReceipt.notes && (
                <div
                  style={{
                    marginTop: 16,
                    paddingTop: 16,
                    borderTop: "1px solid #d9d9d9",
                  }}
                >
                  <Text strong>📝 ໝາຍເຫດ:</Text>
                  <br />
                  <Text>{selectedReceipt.notes}</Text>
                </div>
              )}
            </Card>

            <div style={{ marginTop: 16, textAlign: "right" }}>
              <Card
                size="small"
                style={{ display: "inline-block", minWidth: 250 }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 8,
                  }}
                >
                  <Text>ຍອດລວມ:</Text>
                  <Text>
                    {formatCurrency(
                      selectedReceipt.subtotal,
                      selectedReceipt.currency
                    )}
                  </Text>
                </div>
                {selectedReceipt.discount > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 8,
                      color: "#f56a00",
                    }}
                  >
                    <Text>ສ່ວນຫຼຸດ:</Text>
                    <Text>
                      -
                      {formatCurrency(
                        selectedReceipt.discount,
                        selectedReceipt.currency
                      )}
                    </Text>
                  </div>
                )}
                <Divider style={{ margin: "8px 0" }} />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  <Text strong>ຍອດສຸດທິ:</Text>
                  <Text strong style={{ color: "#f56a00" }}>
                    {formatCurrency(
                      selectedReceipt.total,
                      selectedReceipt.currency
                    )}
                  </Text>
                </div>
                {selectedReceipt.amountDue > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: 8,
                      color: "#ff4d4f",
                    }}
                  >
                    <Text>ຄົງເຫຼືອ:</Text>
                    <Text strong>
                      {formatCurrency(
                        selectedReceipt.amountDue,
                        selectedReceipt.currency
                      )}
                    </Text>
                  </div>
                )}
              </Card>
            </div>
          </div>
        )}
      </Modal>

      {/* Update Payment Modal */}
      <Modal
        title={
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <Title level={3} style={{ margin: 0, color: "#1890ff" }}>
              💰 ອັບເດດການຊຳລະເງິນ
            </Title>
          </div>
        }
        open={activeModal === "payment"}
        onCancel={closeModal}
        footer={null}
        width={600}
        styles={{ content: { borderRadius: 16 } }}
      >
        <Spin spinning={updatePaymentMutation.isLoading}>
          <Form
            form={paymentForm}
            layout="vertical"
            onFinish={handlePaymentSubmit}
          >
            {selectedReceipt && (
              <Alert
                message="ຂໍ້ມູນໃບບິນ"
                description={
                  <div style={{ marginTop: 8 }}>
                    <div>
                      🧾 ເລກທີ:{" "}
                      <Text code>{selectedReceipt.receiptNumber}</Text>
                    </div>
                    <div>
                      💰 ຍອດລວມ:{" "}
                      <Text strong>
                        {formatCurrency(
                          selectedReceipt.total,
                          selectedReceipt.currency
                        )}
                      </Text>
                    </div>
                    <div>
                      ✅ ຊຳລະແລ້ວ:{" "}
                      <Text style={{ color: "#52c41a" }}>
                        {formatCurrency(
                          selectedReceipt.amountPaid,
                          selectedReceipt.currency
                        )}
                      </Text>
                    </div>
                    <div>
                      💸 ຍັງເຫຼືອ:{" "}
                      <Text style={{ color: "#ff4d4f" }}>
                        {formatCurrency(
                          selectedReceipt.amountDue,
                          selectedReceipt.currency
                        )}
                      </Text>
                    </div>
                  </div>
                }
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />
            )}

            <Form.Item
              name="amountPaid"
              label={<Text strong>💵 ຈຳນວນເງິນທີ່ຊຳລະເພີ່ມ</Text>}
              rules={[
                { required: true, message: "ກະລຸນາປ້ອນຈຳນວນເງິນ" },
                { type: "number", min: 0, message: "ຈຳນວນເງິນຕ້ອງຫຼາຍກ່ວາ 0" },
              ]}
            >
              <InputNumber
                style={{ width: "100%", borderRadius: 8 }}
                size="large"
                placeholder="ຈຳນວນເງິນຊຳລະເພີ່ມ"
                formatter={(value: any) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value: any) => value.replace(/\$\s?|(,*)/g, "")}
                addonAfter={
                  selectedReceipt
                    ? getCurrencySymbol(selectedReceipt.currency)
                    : "₭"
                }
              />
            </Form.Item>

            <Form.Item
              name="paymentMethod"
              label={<Text strong>💳 ວິທີການຊຳລະເງິນ</Text>}
            >
              <Select
                placeholder="ເລືອກວິທີການຊຳລະເງິນ"
                size="large"
                style={{ borderRadius: 8 }}
              >
                <Option value="cash">💵 ເງິນສົດ</Option>
                <Option value="transfer">🏦 ໂອນເງິນ</Option>
                <Option value="qr">📱 QR Code</Option>
                <Option value="card">💳 ບັດສະມາຊິກ</Option>
              </Select>
            </Form.Item>

            <Form.Item name="notes" label={<Text strong>📝 ໝາຍເຫດ</Text>}>
              <TextArea
                rows={3}
                placeholder="ໝາຍເຫດເພີ່ມເຕີມ (ບໍ່ບັງຄັບ)"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>

            <Form.Item>
              <Flex justify="center" gap={12}>
                <Button
                  size="large"
                  onClick={closeModal}
                  disabled={updatePaymentMutation.isLoading}
                  style={{ borderRadius: 8, paddingLeft: 32, paddingRight: 32 }}
                >
                  ຍົກເລິກ
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={updatePaymentMutation.isLoading}
                  style={{
                    borderRadius: 8,
                    background:
                      "linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)",
                    border: "none",
                    paddingLeft: 32,
                    paddingRight: 32,
                    fontWeight: 600,
                  }}
                >
                  💾 ແກ້ໄຂ
                </Button>
              </Flex>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Void Receipt Modal */}
      <Modal
        title={
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <Title level={3} style={{ margin: 0, color: "#ff4d4f" }}>
              ❌ ຍົກເລິກບິນເກັບເງິນ
            </Title>
          </div>
        }
        open={activeModal === "void"}
        onCancel={closeModal}
        footer={null}
        width={600}
        styles={{ content: { borderRadius: 16 } }}
      >
        <Spin spinning={voidReceiptMutation.isLoading}>
          {selectedReceipt && (
            <Alert
              message="⚠️ ຄຳເຕືອນ"
              description={
                <div style={{ marginTop: 8 }}>
                  <Text strong>ການຍົກເລິກໃບເກັບເງິນຈະບໍ່ສາມາດຍ້ອນກັບໄດ້</Text>
                  <br />
                  <div style={{ marginTop: 8 }}>
                    <div>
                      🧾 ເລກທີ:{" "}
                      <Text code>{selectedReceipt.receiptNumber}</Text>
                    </div>
                    <div>
                      👤 ລູກຄ້າ:{" "}
                      <Text strong>
                        {selectedReceipt.customer?.name || "N/A"}
                      </Text>
                    </div>
                    <div>
                      💰 ຍອດລວມ:{" "}
                      <Text strong>
                        {formatCurrency(
                          selectedReceipt.total,
                          selectedReceipt.currency
                        )}
                      </Text>
                    </div>
                  </div>
                </div>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}

          <Form form={voidForm} layout="vertical" onFinish={handleVoidSubmit}>
            <Form.Item
              name="voidReason"
              label={<Text strong>📝 ເຫດຜົນໃນການຍົກເລິກ</Text>}
              rules={[
                { required: true, message: "ກະລຸນາປ້ອນເຫດຜົນການຍົກເລິກ" },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="ລະບຸເຫດຜົນໃນການຍົກເລິກ..."
                showCount
                maxLength={500}
                style={{ borderRadius: 8 }}
              />
            </Form.Item>

            <Form.Item>
              <Flex justify="center" gap={12}>
                <Button
                  size="large"
                  onClick={closeModal}
                  disabled={voidReceiptMutation.isLoading}
                  style={{ borderRadius: 8, paddingLeft: 32, paddingRight: 32 }}
                >
                  ຍົກເລິກ
                </Button>
                <Button
                  danger
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={voidReceiptMutation.isLoading}
                  style={{
                    borderRadius: 8,
                    paddingLeft: 32,
                    paddingRight: 32,
                    fontWeight: 600,
                  }}
                >
                  ❌ ຢືນຢັນຍົກເລິກ
                </Button>
              </Flex>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Daily Report Modal */}
      <Modal
        title={
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <Title level={3} style={{ margin: 0, color: "#1890ff" }}>
              📊 ລາຍງານລາຍຮັບປະຈຳວັນ
            </Title>
          </div>
        }
        open={activeModal === "daily-report"}
        onCancel={closeModal}
        footer={[
          <Button key="close" type="primary" onClick={closeModal}>
            ປິດ
          </Button>,
        ]}
        width={1000}
        style={{ top: 20 }}
        styles={{ content: { borderRadius: 16 } }}
      >
        <div style={{ marginBottom: 16 }}>
          <DatePicker
            value={moment(selectedDate)}
            onChange={(date) =>
              setSelectedDate(
                date?.format("YYYY-MM-DD") || moment().format("YYYY-MM-DD")
              )
            }
            style={{ width: 250, borderRadius: 8 }}
            size="large"
          />
        </div>

        <Spin spinning={isDailyReportLoading}>
          {dailyReportData?.data ? (
            <div>
              {/* Summary Cards */}
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={6}>
                  <Card style={{ borderRadius: 12, textAlign: "center" }}>
                    <Statistic
                      title="📋 ຈຳນວນໃບບິນ"
                      value={dailyReportData.data.summary.totalReceipts}
                      prefix={<FileTextOutlined />}
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card style={{ borderRadius: 12, textAlign: "center" }}>
                    <Statistic
                      title="💰 ລາຍຮັບ LAK"
                      value={dailyReportData.data.summary.totalRevenue.LAK}
                      formatter={(value) =>
                        `${new Intl.NumberFormat("lo-LA").format(
                          Number(value)
                        )}`
                      }
                      suffix="₭"
                      valueStyle={{ color: "#52c41a" }}
                      prefix={<DollarOutlined />}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card style={{ borderRadius: 12, textAlign: "center" }}>
                    <Statistic
                      title="💰 ລາຍຮັບ THB"
                      value={dailyReportData.data.summary.totalRevenue.THB}
                      formatter={(value) =>
                        `${new Intl.NumberFormat("th-TH").format(
                          Number(value)
                        )}`
                      }
                      suffix="฿"
                      valueStyle={{ color: "#52c41a" }}
                      prefix={<DollarOutlined />}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card style={{ borderRadius: 12, textAlign: "center" }}>
                    <Statistic
                      title="💰 ລາຍຮັບ USD"
                      value={dailyReportData.data.summary.totalRevenue.USD}
                      formatter={(value) =>
                        `${new Intl.NumberFormat("en-US").format(
                          Number(value)
                        )}`
                      }
                      suffix="$"
                      valueStyle={{ color: "#52c41a" }}
                      prefix={<DollarOutlined />}
                    />
                  </Card>
                </Col>
              </Row>

              {/* Payment Methods */}
              <Card
                title="💳 ແຍກຕາມຊ່ອງທາງການຊຳລະ"
                style={{ marginBottom: 16, borderRadius: 12 }}
              >
                <Row gutter={16}>
                  <Col span={6}>
                    <Card size="small" style={{ backgroundColor: "#f6ffed" }}>
                      <Statistic
                        title="💵 ເງິນສົດ"
                        value={dailyReportData.data.summary.paymentMethods.cash}
                        formatter={(value) =>
                          new Intl.NumberFormat("lo-LA").format(Number(value))
                        }
                        valueStyle={{ color: "#52c41a" }}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card size="small" style={{ backgroundColor: "#e6f7ff" }}>
                      <Statistic
                        title="🏦 ໂອນເງິນ"
                        value={
                          dailyReportData.data.summary.paymentMethods.transfer
                        }
                        formatter={(value) =>
                          new Intl.NumberFormat("lo-LA").format(Number(value))
                        }
                        valueStyle={{ color: "#1890ff" }}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card size="small" style={{ backgroundColor: "#f9f0ff" }}>
                      <Statistic
                        title="📱 QR Code"
                        value={dailyReportData.data.summary.paymentMethods.qr}
                        formatter={(value) =>
                          new Intl.NumberFormat("lo-LA").format(Number(value))
                        }
                        valueStyle={{ color: "#722ed1" }}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card size="small" style={{ backgroundColor: "#fff7e6" }}>
                      <Statistic
                        title="💳 ບັດເຄດິດ"
                        value={dailyReportData.data.summary.paymentMethods.card}
                        formatter={(value) =>
                          new Intl.NumberFormat("lo-LA").format(Number(value))
                        }
                        valueStyle={{ color: "#fa8c16" }}
                      />
                    </Card>
                  </Col>
                </Row>
              </Card>

              {/* Receipts Table */}
              <Card
                title={`📋 ລາຍການໃບເກັບເງິນວັນທີ ${moment(selectedDate).format(
                  "DD/MM/YYYY"
                )}`}
                style={{ borderRadius: 12 }}
              >
                <Table
                  dataSource={dailyReportData.data.receipts}
                  pagination={false}
                  size="small"
                  scroll={{ y: 400 }}
                  rowKey="_id"
                  columns={[
                    {
                      title: "🧾 ເລກທີ",
                      dataIndex: "receiptNumber",
                      width: 120,
                      render: (receiptNumber: string) => (
                        <Text code strong>
                          {receiptNumber}
                        </Text>
                      ),
                    },
                    {
                      title: "👤 ລູກຄ້າ",
                      dataIndex: "customer",
                      render: (customer: any) => customer?.name || "N/A",
                    },
                    {
                      title: "💰 ຍອດລວມ",
                      dataIndex: "total",
                      width: 120,
                      align: "right" as const,
                      render: (total: number, record: any) => (
                        <Text strong style={{ color: "#f56a00" }}>
                          {formatCurrency(total, record.currency)}
                        </Text>
                      ),
                    },
                    {
                      title: "💳 ວິທີຊຳລະ",
                      dataIndex: "paymentMethod",
                      width: 100,
                      render: (method: string) => {
                        const config = getPaymentMethodConfig(method);
                        return (
                          <Tag color={config.color} style={{ fontWeight: 500 }}>
                            {config.text}
                          </Tag>
                        );
                      },
                    },
                    {
                      title: "🕐 ເວລາ",
                      dataIndex: "createdAt",
                      width: 80,
                      render: (date: string) => (
                        <Text>{moment(date).format("HH:mm")}</Text>
                      ),
                    },
                  ]}
                />
              </Card>
            </div>
          ) : (
            <Empty description="ບໍ່ມີຂໍ້ມູນ" />
          )}
        </Spin>
      </Modal>

      {/* Create Receipt Modal */}
      <Modal
        title={
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <Title level={3} style={{ margin: 0, color: "#1890ff" }}>
              ➕ ສ້າງໃບເກັບເງິນໃໝ່
            </Title>
          </div>
        }
        open={activeModal === "create"}
        onCancel={() => {
          closeModal();
          resetReceiptForm();
        }}
        footer={null}
        width={1200}
        style={{ top: 20 }}
        styles={{ content: { borderRadius: 16 } }}
      >
        <Spin spinning={createReceiptMutation.isLoading}>
          <Form
            form={createForm}
            layout="vertical"
            onFinish={handleCreateReceiptSubmit}
          >
            <Tabs defaultActiveKey="1">
              {/* Tab 1: Customer */}
              <Tabs.TabPane
                tab={
                  <span>
                    {/* <UserOutlined /> */}
                    👤 ເລືອກລູກຄ້າ
                  </span>
                }
                key="1"
              >
                <Card
                  title="ຂໍ້ມູນລູກຄ້າ"
                  style={{ borderRadius: 12 }}
                  extra={
                    receiptFormData.customer && (
                      <Badge count={1} style={{ backgroundColor: "#52c41a" }}>
                        <CheckCircleOutlined style={{ color: "#52c41a" }} />
                      </Badge>
                    )
                  }
                >
                  <AutoComplete
                    style={{ width: "100%", marginBottom: 16 }}
                    placeholder="🔍 ຄົ້ນຫາລູກຄ້າ (ຊື່, ລະຫັດ, ເບີໂທ)"
                    value={customerSearchText}
                    onChange={setCustomerSearchText}
                    size="large"
                    options={getFilteredCustomers?.map((customer: any) => ({
                      value: `${customer.name} (${customer.customerId})`, // แสดงทั้งชื่อและรหัส
                      label: (
                        <div
                          onClick={() => {
                            setReceiptFormData((prev) => ({
                              ...prev,
                              customer,
                            }));
                            setCustomerSearchText(
                              `${customer.name} (${customer.customerId})`
                            );
                          }}
                        >
                          <div style={{ fontWeight: 500 }}>{customer.name}</div>
                          <div style={{ fontSize: "12px", color: "#666" }}>
                            🆔 {customer.customerId} | 📞 {customer.phone}
                          </div>
                        </div>
                      ),
                    }))}
                    filterOption={false}
                  />

                  {receiptFormData.customer && (
                    <Card
                      size="small"
                      style={{
                        backgroundColor: "#f6ffed",
                        border: "1px solid #b7eb8f",
                        borderRadius: 8,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <Avatar
                            size={40}
                            icon={<UserOutlined />}
                            style={{
                              backgroundColor: "#52c41a",
                              marginRight: 12,
                            }}
                          />
                          <div>
                            <div style={{ fontWeight: 500, fontSize: "16px" }}>
                              {receiptFormData.customer.name}
                            </div>
                            <div style={{ color: "#666" }}>
                              🆔 {receiptFormData.customer.customerId} | 📞{" "}
                              {receiptFormData.customer.phone}
                            </div>
                          </div>
                        </div>
                        <Button
                          size="small"
                          danger
                          onClick={() => {
                            setReceiptFormData((prev) => ({
                              ...prev,
                              customer: null,
                            }));
                            setCustomerSearchText("");
                          }}
                          style={{ borderRadius: 6 }}
                        >
                          ❌ ລຶບ
                        </Button>
                      </div>
                    </Card>
                  )}
                </Card>
              </Tabs.TabPane>

              {/* Tab 2: Services */}
              <Tabs.TabPane
                tab={
                  <span>
                    {/* <ShoppingCartOutlined /> */}
                    🛍️ ເລືອກບໍລິການ
                    {receiptFormData.items.length > 0 && (
                      <Badge
                        count={receiptFormData.items.length}
                        style={{ marginLeft: 8 }}
                      />
                    )}
                  </span>
                }
                key="2"
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Card
                      title="🛍️ ລາຍການບໍລິການ"
                      style={{ height: 500, borderRadius: 12 }}
                      bodyStyle={{ padding: 0, overflow: "auto", height: 420 }}
                      extra={
                        <Input
                          placeholder="ຄົ້ນຫາຄອສ..."
                          prefix={
                            <SearchOutlined style={{ color: "#bfbfbf" }} />
                          }
                          allowClear
                          onPressEnter={(e) =>
                            handleSearchCourse(e.currentTarget.value)
                          }
                          style={{ borderRadius: "8px" }}
                        />
                      }
                    >
                      <div style={{ padding: 16 }}>
                        {coursesData?.data?.courses?.map((course: any) => (
                          <Card
                            key={course._id}
                            size="small"
                            style={{
                              marginBottom: 12,
                              borderRadius: 8,
                              border: "1px solid #e8e8e8",
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 500,
                                marginBottom: 8,
                                color: "#1890ff",
                              }}
                            >
                              {course.name}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#666",
                                marginBottom: 12,
                              }}
                            >
                              📂 {course?.category?.name}
                            </div>
                            {course.pricing?.map((pkg: any, idx: number) => (
                              <div
                                key={idx}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "8px 12px",
                                  backgroundColor: "#fafafa",
                                  borderRadius: 6,
                                  marginBottom: 8,
                                  border: "1px solid #f0f0f0",
                                }}
                              >
                                <div>
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      fontWeight: 500,
                                    }}
                                  >
                                    🎯 {pkg.sessions} ຄັ້ງ ({pkg.validityDays}{" "}
                                    ມື້)
                                  </div>
                                  <div
                                    style={{
                                      fontWeight: 600,
                                      color: "#f56a00",
                                    }}
                                  >
                                    💰 {formatCurrency(pkg.price.LAK, "LAK")}
                                  </div>
                                </div>
                                <Button
                                  size="small"
                                  type="primary"
                                  onClick={() => addItemToReceipt(course, pkg)}
                                  style={{ borderRadius: 6 }}
                                >
                                  ➕ ເພີ່ມ
                                </Button>
                              </div>
                            ))}
                          </Card>
                        ))}
                      </div>
                    </Card>
                  </Col>

                  <Col span={12}>
                    <Card
                      title={`🛒 ລາຍການທີ່ເລືອກ (${receiptFormData.items.length})`}
                      style={{ height: 500, borderRadius: 12 }}
                      bodyStyle={{ padding: 0, overflow: "auto", height: 420 }}
                    >
                      <div style={{ padding: 16 }}>
                        {receiptFormData.items.length === 0 ? (
                          <div
                            style={{
                              textAlign: "center",
                              color: "#666",
                              padding: "60px 0",
                            }}
                          >
                            <ShoppingCartOutlined
                              style={{ fontSize: 48, marginBottom: 16 }}
                            />
                            <div>ຍັງບໍ່ມີລາຍການທີ່ເລືອກ</div>
                          </div>
                        ) : (
                          receiptFormData.items.map((item, index) => (
                            <Card
                              key={index}
                              size="small"
                              style={{
                                marginBottom: 12,
                                borderRadius: 8,
                                border: "1px solid #e8e8e8",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{
                                      fontWeight: 500,
                                      color: "#1890ff",
                                    }}
                                  >
                                    {item.course.name}
                                  </div>
                                  <div
                                    style={{ fontSize: "12px", color: "#666" }}
                                  >
                                    🎯 {item.packageOption.sessions} ຄັ້ງ
                                  </div>
                                  <div
                                    style={{
                                      fontWeight: 500,
                                      color: "#f56a00",
                                    }}
                                  >
                                    💰{" "}
                                    {formatCurrency(
                                      item.packageOption.price.LAK,
                                      "LAK"
                                    )}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  <InputNumber
                                    size="small"
                                    min={1}
                                    value={item.quantity}
                                    onChange={(value) =>
                                      updateItemQuantity(index, value || 1)
                                    }
                                    style={{ width: 60 }}
                                  />
                                  <Button
                                    size="small"
                                    danger
                                    icon={<MinusCircleOutlined />}
                                    onClick={() => removeItemFromReceipt(index)}
                                    style={{ borderRadius: 4 }}
                                  />
                                </div>
                              </div>
                              <div
                                style={{
                                  textAlign: "right",
                                  marginTop: 12,
                                  paddingTop: 8,
                                  borderTop: "1px solid #f0f0f0",
                                }}
                              >
                                <Text strong style={{ color: "#f56a00" }}>
                                  💰 ລວມ:{" "}
                                  {formatCurrency(
                                    item.packageOption.price.LAK *
                                      item.quantity,
                                    "LAK"
                                  )}
                                </Text>
                              </div>
                            </Card>
                          ))
                        )}
                      </div>
                    </Card>
                  </Col>
                </Row>
              </Tabs.TabPane>

              {/* Tab 3: Payment */}
              <Tabs.TabPane
                tab={
                  <span>
                    {/* <DollarOutlined /> */}
                    💳 ຊຳລະເງິນ
                  </span>
                }
                key="3"
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Card
                      title="💳 ລາຍລະອຽດການຊຳລະ"
                      style={{ borderRadius: 12 }}
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="currency"
                            label={<Text strong>💱 ສະກຸນເງິນ</Text>}
                            initialValue="LAK"
                            rules={[
                              {
                                required: true,
                                message: "ກະລຸນາເລືອກສະກຸນເງິນ",
                              },
                            ]}
                          >
                            <Select size="large" style={{ borderRadius: 8 }}>
                              <Option value="LAK">🇱🇦 LAK (₭)</Option>
                              <Option value="THB">🇹🇭 THB (฿)</Option>
                              <Option value="USD">🇺🇸 USD ($)</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="paymentMethod"
                            label={<Text strong>💳 ວິທີການຊຳລະ</Text>}
                            initialValue="cash"
                            rules={[
                              {
                                required: true,
                                message: "ກະລຸເລືອກວິທີການຊຳລະ",
                              },
                            ]}
                          >
                            <Select size="large" style={{ borderRadius: 8 }}>
                              <Option value="cash">💵 ເງິນສົດ</Option>
                              <Option value="transfer">🏦 ໂອນເງິນ</Option>
                              <Option value="qr">📱 QR Code</Option>
                              <Option value="card">💳 ບັດສະມາຊິກ</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item
                        name="discount"
                        label={<Text strong>🎁 ສ່ວນຫຼຸດ (%)</Text>}
                        initialValue={0}
                      >
                        <InputNumber
                          min={0}
                          max={100}
                          size="large"
                          style={{ width: "100%", borderRadius: 8 }}
                          placeholder="ส่วนลด (0-100%)"
                        />
                      </Form.Item>

                      <Form.Item
                        name="notes"
                        label={<Text strong>📝 ໝາຍເຫດ</Text>}
                      >
                        <TextArea
                          rows={3}
                          placeholder="ໝາຍເຫດ (ບໍ່ບັງຄັບ)"
                          style={{ borderRadius: 8 }}
                        />
                      </Form.Item>
                    </Card>
                  </Col>

                  <Col span={12}>
                    <Card
                      title="📊 ສະຫລຸບການສັ່ງຊື້"
                      style={{ borderRadius: 12 }}
                    >
                      <Form.Item shouldUpdate>
                        {({ getFieldValue }) => {
                          const currency = getFieldValue("currency") || "LAK";
                          const discount = getFieldValue("discount") || 0;
                          const subtotal = calculateSubtotal(currency);
                          const discountAmount = (subtotal * discount) / 100;
                          const total = subtotal - discountAmount;

                          return (
                            <div>
                              <div style={{ marginBottom: 16 }}>
                                <Row
                                  justify="space-between"
                                  style={{ marginBottom: 8 }}
                                >
                                  <Text>📦 ລາຍການ:</Text>
                                  <Text strong>
                                    {receiptFormData.items.length} ລາຍການ
                                  </Text>
                                </Row>
                                <Row
                                  justify="space-between"
                                  style={{ marginBottom: 8 }}
                                >
                                  <Text>💰 ຍອດລວມ:</Text>
                                  <Text strong>
                                    {formatCurrency(subtotal, currency)}
                                  </Text>
                                </Row>
                                {discount > 0 && (
                                  <Row
                                    justify="space-between"
                                    style={{ marginBottom: 8 }}
                                  >
                                    <Text style={{ color: "#f56a00" }}>
                                      🎁 ສ່ວນຫຼຸດ ({discount}%):
                                    </Text>
                                    <Text strong style={{ color: "#f56a00" }}>
                                      -
                                      {formatCurrency(discountAmount, currency)}
                                    </Text>
                                  </Row>
                                )}
                                <Divider style={{ margin: "12px 0" }} />
                                <Row
                                  justify="space-between"
                                  style={{
                                    fontSize: "18px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  <Text strong>💸 ຍອດສຸດທິ:</Text>
                                  <Text strong style={{ color: "#f56a00" }}>
                                    {formatCurrency(total, currency)}
                                  </Text>
                                </Row>
                              </div>

                              {receiptFormData.items.length > 0 && (
                                <Card
                                  size="small"
                                  style={{
                                    backgroundColor: "#f6ffed",
                                    border: "1px solid #b7eb8f",
                                    borderRadius: 8,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      fontWeight: 500,
                                      marginBottom: 8,
                                    }}
                                  >
                                    📋 ລາຍການສິນຄ້າ:
                                  </div>
                                  {receiptFormData.items.map((item, index) => (
                                    <div
                                      key={index}
                                      style={{
                                        fontSize: "11px",
                                        color: "#666",
                                        marginBottom: 4,
                                      }}
                                    >
                                      • {item.course.name} (
                                      {item.packageOption.sessions} ຄັ້ງ) x
                                      {item.quantity}
                                    </div>
                                  ))}
                                </Card>
                              )}
                            </div>
                          );
                        }}
                      </Form.Item>
                    </Card>
                  </Col>
                </Row>

                <div style={{ textAlign: "center", marginTop: 24 }}>
                  <Flex justify="center" gap={16}>
                    <Button
                      size="large"
                      onClick={() => {
                        closeModal();
                        resetReceiptForm();
                      }}
                      disabled={createReceiptMutation.isLoading}
                      style={{
                        borderRadius: 8,
                        paddingLeft: 32,
                        paddingRight: 32,
                      }}
                    >
                      ❌ ຍົກເລິກ
                    </Button>
                    <Button
                      type="primary"
                      size="large"
                      htmlType="submit"
                      loading={createReceiptMutation.isLoading}
                      disabled={
                        !receiptFormData.customer ||
                        receiptFormData.items.length === 0
                      }
                      style={{
                        borderRadius: 8,
                        background:
                          "linear-gradient(135deg, #52c41a 0%, #73d13d 100%)",
                        border: "none",
                        paddingLeft: 32,
                        paddingRight: 32,
                        fontWeight: 600,
                      }}
                    >
                      ✅ ສ້າງໃບບິນ
                    </Button>
                  </Flex>
                </div>
              </Tabs.TabPane>
            </Tabs>
          </Form>
        </Spin>
      </Modal>

      {/* Custom CSS */}
      <style>{`
        .row-pending {
          background-color: #fff7e6 !important;
        }
        .row-paid {
          background-color: #f6ffed !important;
        }
        .ant-table-tbody > tr:hover.row-pending > td {
          background-color: #ffe7ba !important;
        }
        .ant-table-tbody > tr:hover.row-paid > td {
          background-color: #d9f7be !important;
        }
      `}</style>
    </div>
  );
};

export default ReceiptsPage;
